import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function ParentLinkField({ value, onChange, branchId }) {
  const { user } = useAuth();
  const [parents, setParents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ name: "", email: "", password: "" });
  const canCreate = ["master_admin", "super_admin", "principal"].includes(user?.role);

  useEffect(() => { api.get("/users/parents").then((r) => setParents(r.data)).catch(() => {}); }, []);

  const shown = parents.filter((p) => !branchId || p.branch_id === branchId || p.id === value);

  async function create() {
    try {
      const { data } = await api.post("/users/parents", { ...np, branch_id: branchId });
      setParents((cur) => [...cur, data]);
      setTimeout(() => onChange(data.id), 0);
      setCreating(false);
      setNp({ name: "", email: "", password: "" });
      toast.success("Parent login created and linked");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
      <Label>Parent login (portal access)</Label>
      <p className="text-xs text-slate-500 mt-0.5">Parents also see this child automatically if their login email matches the parent email above.</p>
      <div className="flex gap-2 mt-2">
        <Select value={value || "none"} onValueChange={(v) => { if (!v) return; onChange(v === "none" ? "" : v); }}>
          <SelectTrigger data-testid="student-form-parent-link" className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not linked</SelectItem>
            {shown.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} • {p.email}</SelectItem>)}
          </SelectContent>
        </Select>
        {canCreate && (
          <Button type="button" variant="outline" data-testid="new-parent-toggle" onClick={() => setCreating((v) => !v)} className="border-violet-200 text-violet-700 shrink-0">
            {creating ? "Cancel" : "New login"}
          </Button>
        )}
      </div>
      {creating && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          <Input data-testid="new-parent-name" placeholder="Name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className="bg-white" />
          <Input data-testid="new-parent-email" placeholder="Email" type="email" value={np.email} onChange={(e) => setNp({ ...np, email: e.target.value })} className="bg-white" />
          <Input data-testid="new-parent-password" placeholder="Password" value={np.password} onChange={(e) => setNp({ ...np, password: e.target.value })} className="bg-white" />
          <Button type="button" data-testid="new-parent-save" onClick={create} disabled={!np.name || !np.email || np.password.length < 4} className="sm:col-span-3 bg-violet-600 hover:bg-violet-700">Create & link parent</Button>
        </div>
      )}
    </div>
  );
}
